import Box from "@mui/material/Box";
import type { SxProps, Theme } from "@mui/material/styles";
import Typography from "@mui/material/Typography";

import { StyledAnchor } from "@/components/common/styles";
import { ArrowIcon } from "@/components/marketing/MarketingIcons";
import {
  METRIC_ARROW_CLASS,
  METRIC_CARD_HOVER,
} from "@/components/marketing/metricCardMotion";
import {
  marketingHref,
  newTabProps,
} from "@/components/marketing/marketingLinks";
import {
  FAQ_RAIL,
  MID,
  NOT_WIDE,
  TINY,
  WIDE,
  marketingPalette,
} from "@/components/marketing/tokens";
import type { Card } from "@/content/landing/types";
import {
  LandingButton,
  LandingImage,
  LandingSection,
  LandingVisual,
} from "../LandingPrimitives";
import { sectionPad } from "./landingRun";
import { RichRuns } from "./splitText";

/**
 * `.faq-main_cms_wrap` and `.c-cloneable` together: on the rotating page the
 * panels stand inside the grey FAQ well, 104 under its top and 100 into the
 * cloneable's own padding, so the stack opens on the sum and closes on nothing,
 * the control well under it carrying the cloneable's close.
 */
const WELL_LEAD = {
  pt: "204px",
  [NOT_WIDE]: { pt: "128px" },
  [TINY]: { pt: "112px" },
} satisfies SxProps<Theme>;
import type { Cards } from "./cardsShared";
import { Lines } from "@/components/marketing/Lines";

/** The grounds the closing feature bands take, from the first unless the block runs them to the end. */
const PANELS = [
  {
    ground: marketingPalette.featureAzure,
    chip: marketingPalette.featureAzureChip,
    ink: marketingPalette.featureAzureInk,
    rule: marketingPalette.featureAzureChip,
  },
  {
    ground: marketingPalette.featureLilac,
    chip: marketingPalette.featureLilacChip,
    ink: marketingPalette.featureLilacInk,
    rule: marketingPalette.featureLilacChip,
  },
  {
    ground: marketingPalette.featurePeach,
    chip: marketingPalette.featurePeachChip,
    ink: marketingPalette.featurePeachInk,
    rule: marketingPalette.featurePeachRule,
  },
];

type Panel = (typeof PANELS)[number];

/** `feat-2-metric_wrap`: the figures the "Fast X" panel closes on. */
function FeatureMetrics({ card, panel }: { card: Card; panel?: Panel }) {
  if (!card.metrics?.length) {
    return null;
  }
  return (
    <Box
      sx={{
        display: "flex",
        gap: "12px",
        mt: "8px",
        // `.feat-2-metric_wrap` on a phone: centred and wrapping, 16 between rows.
        [NOT_WIDE]: { justifyContent: "center" },
        [TINY]: { flexWrap: "wrap", rowGap: "16px" },
      }}
    >
      {card.metrics.map((metric) => (
        <Box
          key={metric.label}
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            gap: "4px",
            px: "16px",
            py: "12.8px",
            borderRadius: "16px",
            backgroundColor: panel?.chip,
            // `.feat-2-metric_item` on a phone: no box, its lines centred.
            [TINY]: {
              p: 0,
              alignItems: "center",
              backgroundColor: "transparent",
            },
          }}
        >
          {/* Both lines keep the body's 150%, so the phone sizes bring their
              own leading. The figure spans the chip, so it centres over a
              longer label wherever the card centres its text. */}
          <Box
            sx={{
              width: 1,
              fontSize: 17,
              fontWeight: 600,
              lineHeight: "150%",
              color: panel?.ink,
              [TINY]: { fontSize: 18 },
            }}
          >
            {metric.value}
          </Box>
          <Box
            sx={{
              fontSize: 15,
              lineHeight: "150%",
              [TINY]: { fontSize: 13 },
            }}
          >
            {metric.label}
          </Box>
        </Box>
      ))}
    </Box>
  );
}

/** `feat-3-metric_item`: the two link panels inside a published "X List" card. */
function FeatureTiles({ card, panel }: { card: Card; panel?: Panel }) {
  if (!card.tiles?.length) {
    return null;
  }
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "16px",
        width: 1,
        mt: "8px",
      }}
    >
      {card.tiles.map((tile) => (
        <StyledAnchor
          key={tile.title}
          href={marketingHref(tile.cta.href ?? "")}
          {...newTabProps(tile.cta)}
          sx={[
            {
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              // The panel may be centred; the tiles inside it never are.
              textAlign: "left",
              gap: "8px",
              p: "19.2px",
              borderRadius: "16px",
              border: `1px solid ${panel?.rule}`,
              backgroundColor: panel?.chip,
            },
            METRIC_CARD_HOVER,
          ]}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
            {tile.icon ? (
              <LandingImage
                src={tile.icon}
                sx={{ height: "19.2px", width: "19.2px", opacity: 0.35 }}
              />
            ) : null}
            <Box
              sx={{
                fontSize: 17,
                fontWeight: 500,
                lineHeight: "25.5px",
                [TINY]: { fontSize: 16, lineHeight: "22px" },
              }}
            >
              <Lines text={tile.title} />
            </Box>
          </Box>
          <Box
            sx={{
              fontSize: 14,
              lineHeight: "20px",
              [TINY]: { fontSize: 13 },
              color: marketingPalette.inkSubtle,
            }}
          >
            {tile.body}
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              mt: "8px",
              fontSize: 12,
              fontWeight: 500,
              lineHeight: "18px",
              textTransform: "uppercase",
              color: panel?.ink,
            }}
          >
            {tile.cta.label}
            <ArrowIcon className={METRIC_ARROW_CLASS} />
          </Box>
        </StyledAnchor>
      ))}
    </Box>
  );
}

/** `is-featured_fixed`: in ems, so the measure holds its wrap once the size steps down. */
const FEATURE_TITLE = {
  fontSize: 36,
  fontWeight: 500,
  lineHeight: "48px",
  letterSpacing: "-1.44px",
  maxWidth: "9.204em",
  [NOT_WIDE]: { lineHeight: "40px" },
  [TINY]: { fontSize: 27, lineHeight: "35.1px", letterSpacing: "-1.08px" },
} satisfies SxProps<Theme>;

function FeatureCopy({
  card,
  panel,
  rich,
}: {
  card: Card;
  panel?: Panel;
  rich?: true;
}) {
  const line = rich ? RICH_LINE : PANEL_LINE;
  return (
    <Box
      sx={{
        // `.feature_card-left`: 800 wide before the row shares itself out.
        flex: "1 1 800px",
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        gap: "12px",
        [NOT_WIDE]: {
          flex: "0 0 100%",
          alignItems: "center",
          textAlign: "center",
          gap: "16px",
        },
      }}
    >
      {card.label ? (
        <Box
          sx={{
            px: "15.2px",
            py: "7.2px",
            borderRadius: "16px",
            backgroundColor: panel?.chip,
            color: panel?.ink,
            fontSize: 14,
            fontWeight: 500,
            lineHeight: "14px",
            textTransform: "uppercase",
          }}
        >
          {card.label}
        </Box>
      ) : null}
      <Typography component="h2" sx={FEATURE_TITLE}>
        <Lines text={card.title} />
      </Typography>
      {card.body ? <Typography sx={line}>{card.body}</Typography> : null}
      {card.paragraphs?.map((paragraph, index) => (
        <Typography key={index} sx={line}>
          <RichRuns text={paragraph} links={card.links} />
        </Typography>
      ))}
      {card.cta ? <LandingButton cta={card.cta} small /> : null}
      <FeatureMetrics card={card} panel={panel} />
      <FeatureTiles card={card} panel={panel} />
    </Box>
  );
}

function FeaturePanel({
  card,
  at,
  rich,
}: {
  card: Card;
  at: number;
  rich?: true;
}) {
  const panel = PANELS[at];
  return (
    <Box
      sx={{
        p: "64px",
        borderRadius: "32px",
        position: "relative",
        backgroundColor: panel?.ground,
        // For the `50cqw` the row below measures itself against.
        containerType: "inline-size",
        // The resting place follows the ground's index, not the panel's
        // position, so a block that runs to the end starts part-way down.
        [WIDE]: { position: "sticky", top: `${96 + 32 * at}px` },
        [NOT_WIDE]: { p: "32px" },
        [TINY]: { py: "19.2px", px: "11.2px" },
      }}
    >
      <Box
        sx={{
          // `.feature_card-content`: the copy asks for 800 and the animation
          // for the whole row, and the two shrink in proportion to share it
          // (460 and 668 at the wide step, 376 and 360 at 1000). Column gap
          // only: wrapped below 992, the animation sits flush under the copy.
          display: "flex",
          columnGap: "32px",
          alignItems: "center",
          minHeight: "50cqw",
          [NOT_WIDE]: { flexWrap: "wrap", minHeight: 0 },
        }}
      >
        <FeatureCopy card={card} panel={panel} rich={rich} />
        {card.image ? (
          <LandingVisual
            src={card.image}
            sx={{
              flex: "1 1 100%",
              // The row's floor again: an animation with no definite box grows
              // to its own aspect and takes the card with it.
              height: "50cqw",
              [NOT_WIDE]: { flex: "0 0 100%" },
              [TINY]: { height: "80cqw" },
            }}
          />
        ) : null}
      </Box>
    </Box>
  );
}

/**
 * `.feature_card-body`: the panel's line, 15 on 150% with the page's 16 under
 * it, which the card step drops; 14 on a phone.
 */
const PANEL_LINE = {
  fontSize: 15,
  lineHeight: "22.5px",
  color: marketingPalette.inkMuted,
  maxWidth: "400px",
  mb: "16px",
  [MID]: { mb: 0 },
  [TINY]: { fontSize: 14, lineHeight: "21px" },
} satisfies SxProps<Theme>;

/**
 * `.w-richtext` in the panel, which is how the use-case pages publish the copy:
 * the page's 16 on 24 in the page's ink at every width, each paragraph closing
 * 16 under itself, 12 on a phone, and the column's own width rather than the
 * line's 400. Only `.feature_card-body` sets the grey.
 */
const RICH_LINE = {
  fontSize: 16,
  lineHeight: "24px",
  color: marketingPalette.ink,
  mb: "16px",
  [TINY]: { mb: "12px" },
} satisfies SxProps<Theme>;

/**
 * `_w-cards-list`: the stack of tinted panels a product page closes on. The
 * narrow frame is the one the FAQ rail carries, and it centres its copy.
 */
export default function CardsFeature({
  block,
  continues,
}: {
  block: Cards;
  continues: boolean;
}) {
  const narrow = block.frame === "narrow";
  const offset = block.panelsFromEnd ? PANELS.length - block.items.length : 0;
  return (
    <LandingSection
      padding={
        continues || block.pad === "well"
          ? "none"
          : block.pad
            ? sectionPad(block.pad)
            : narrow
              ? "bottom"
              : "both"
      }
      ground={block.pad === "well" ? "grey" : undefined}
      sx={block.pad === "well" ? WELL_LEAD : undefined}
    >
      <Box
        sx={[
          {
            display: "flex",
            flexDirection: "column",
            gap: "96px",
            // `_w-cards-list` insets the panels past the section's own gutter,
            // and both frames give that inset back at the tiny step.
            px: "20px",
            [NOT_WIDE]: { gap: "64px" },
            [TINY]: { gap: "32px", px: 0 },
          },
          // `width` as well as `maxWidth`: an auto cross-axis margin takes a
          // column flex item off `stretch`.
          narrow
            ? {
                width: 1,
                maxWidth: FAQ_RAIL,
                mx: "auto",
                textAlign: "center",
                px: "36px",
                [TINY]: { px: "16px" },
              }
            : {},
        ]}
      >
        {block.items.map((card, index) => (
          <FeaturePanel
            key={card.title}
            card={card}
            at={Math.max(0, offset + index)}
            rich={block.richCopy}
          />
        ))}
      </Box>
    </LandingSection>
  );
}
